import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Plus, Pencil, Trash2, Save, X, Sparkles } from "lucide-react";
import Field from "./Field";

const FIELDS = [
  { name: "title", label: "Title", required: true },
  { name: "slug", label: "Slug (auto)" },
  { name: "subsubsection", label: "Subsection (e.g. life)" },
  { name: "excerpt", label: "Excerpt", type: "textarea" },
  { name: "tags", label: "Tags (comma separated)", type: "csv" },
  { name: "cover_image", label: "Cover Image", type: "image" },
  { name: "url", label: "Original Article URL" },
  { name: "ai_assisted", label: "AI-assisted?", type: "bool" },
  { name: "published", label: "Published?", type: "bool" },
  { name: "content", label: "Content (Markdown)", type: "textarea", required: true }
];

function empty() {
  return { title: "", slug: "", subsubsection: "", excerpt: "", tags: [], cover_image: "", url: "", ai_assisted: false, published: true, content: "" };
}

export default function BlogEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState("technical and conversational");
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/blog?published_only=false");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...empty(), ...p }); setEditing(p); };
  const close = () => { setEditing(null); setForm(empty()); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/blog", form);
      else await api.put(`/blog/${editing.id}`, form);
      toast.success("Saved.");
      close();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`);
    toast.success("Deleted.");
    load();
  };

  const generate = async () => {
    if (!aiTopic.trim()) { toast.error("Topic required"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/blog-draft", { topic: aiTopic, tone: aiTone, length: "medium" });
      const content = data.content || "";
      // try to extract first H1
      const titleMatch = content.match(/^#\s+(.+)$/m);
      const title = titleMatch ? titleMatch[1].trim() : aiTopic;
      setForm({ ...empty(), title, content, ai_assisted: true, published: false });
      setEditing("new");
      setAiOpen(false);
      setAiTopic("");
      toast.success("Draft generated. Review before publishing.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI draft failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-2xl">Blog</h2>
        <div className="flex gap-3">
          <button onClick={() => setAiOpen(true)} className="btn-ghost" data-testid="admin-blog-ai"><Sparkles className="w-4 h-4" /> AI Draft</button>
          <button onClick={openNew} className="btn-volt" data-testid="admin-blog-new"><Plus className="w-4 h-4" /> New Post</button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="card-tech p-4 flex items-start justify-between gap-4" data-testid={`admin-blog-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <p className="overline">{p.ai_assisted ? "AI" : "HUMAN"}</p>
                {!p.published && <span className="tag-chip">DRAFT</span>}
              </div>
              <p className="font-display font-semibold truncate mt-1">{p.title}</p>
              <p className="text-fog-300 text-sm truncate">{p.excerpt}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="btn-ghost"><Pencil className="w-4 h-4" /> Edit</button>
              <button onClick={() => remove(p.id)} className="btn-ghost text-red-300 border-red-400/40"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-fog-300">No posts yet.</p>}
      </div>

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur flex items-center justify-center p-4" data-testid="admin-ai-modal">
          <div className="card-tech max-w-lg w-full">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">AI Blog Draft (Claude Sonnet 4.5)</h3>
              <button onClick={() => setAiOpen(false)} className="text-fog-300 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="overline block mb-1">Topic</label>
                <input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="How multi-agent RAG beats single-shot LLMs…" data-testid="ai-topic-input" />
              </div>
              <div>
                <label className="overline block mb-1">Tone</label>
                <input value={aiTone} onChange={(e) => setAiTone(e.target.value)} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setAiOpen(false)} className="btn-ghost">Cancel</button>
              <button onClick={generate} disabled={aiLoading} className="btn-volt" data-testid="ai-generate-btn">
                <Sparkles className="w-4 h-4" /> {aiLoading ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur flex items-center justify-center p-4">
          <div className="card-tech max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{editing === "new" ? "New Post" : "Edit Post"}</h3>
              <button onClick={close} className="text-fog-300 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {FIELDS.map((f) => (
                <Field key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
              ))}
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={close} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-volt" data-testid="admin-blog-save"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
